import { ChevronDown } from '@/components/ui/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import {
  pickerContentClass,
  pickerDisablableItemBaseClass,
  pickerHeroWrapperClass,
  pickerItemActiveClass,
  pickerItemDisabledClass,
  pickerItemInactiveClass,
  pillWidthClass,
} from '../dropdownStyles';

import { SubTab } from './data';

interface SupinInletDropdownProps {
  tab: SubTab;
  vehicleSubTab: string;
  supinInletType: string;
  setVehicleSubTab: (subTab: string) => void;
  setSupinInletType: (type: string) => void;
}

export function SupinInletDropdown({
  tab,
  vehicleSubTab,
  supinInletType,
  setVehicleSubTab,
  setSupinInletType,
}: SupinInletDropdownProps) {
  // Four active inlets (K1–K4) get hero cards modeled after the
  // Vehicle Domain dropdown items (full-height card, hero on
  // the right, label + subtitle on the left). K5
  // (Streamline-Traced/Busemann) is commented out below — its
  // Fortran solve exceeds the runner timeout (issue #26); the
  // full UI entry is preserved for when it's re-enabled.
  const SUPIN_INLET_TYPES: {
    value: string;
    label: string;
    subLabel: string;
    hero?: string;
    disabled?: boolean;
  }[] = [
    {
      value: 'K3',
      label: 'Spike Inlet',
      subLabel: 'Axially Symmetric',
      hero: '/supin-spike-inlet-hero.png',
    },
    {
      value: 'K4',
      label: 'Rectangular Inlet',
      subLabel: '2D Bifurcated',
      hero: '/supin-rectangular-inlet-hero.png',
    },
    {
      value: 'K1',
      label: 'Transonic Inlet',
      subLabel: 'Axially Symmetric',
      hero: '/supin-transonic-inlet-hero.png',
    },
    // K2 disabled — its STL freezes the viewer (heavy translucent
    // double-sided render locks the main thread, total tab
    // lockup), even after the mesh was coarsened (issue #26).
    // Re-enable once the viewer can handle it (out-of-row storage +
    // render LOD).
    // {
    //   value: 'K2',
    //   label: 'External Compression Inlet',
    //   subLabel: '2D External Compression',
    // },
    // K5 disabled — Streamline-Traced (Busemann) solve exceeds the
    // runner timeout (issue #26). Re-enable once the K5 solve is
    // bounded/sped up.
    // {
    //   value: 'K5',
    //   label: 'Streamline-Traced Inlet',
    //   subLabel: 'Busemann Inlet',
    //   disabled: true,
    // },
  ];
  const isActive = vehicleSubTab === tab.value;
  // Always show a hero on the trigger: the active inlet's, else the
  // first inlet that has one.
  const supinTriggerHero =
    SUPIN_INLET_TYPES.find((o) => o.value === supinInletType)?.hero ??
    SUPIN_INLET_TYPES.find((o) => o.hero)?.hero;
  return (
    <DropdownMenu key={tab.value}>
      {/* NASA SUPIN dropdown stays interactive even after an
          artifact has been generated, so users can iterate on
          inlet type without starting a new conversation. */}
      <DropdownMenuTrigger asChild>
        <button
          title="NASA SUPIN — Supersonic Inlets (License Restrictions apply)"
          className={cn(
            pillWidthClass,
            'flex items-center justify-center gap-2 self-stretch overflow-hidden rounded-md border px-4 text-center text-sm transition-colors',
            isActive
              ? 'border-[#107d40] bg-[#070909] text-[#107d40] hover:border-[#107d40] hover:bg-[#0f3d24]'
              : 'border-[#737373] text-telekinesis-neutral-50 hover:border-[#107d40] hover:bg-[#0f3d24]',
          )}
        >
          <div className="flex flex-col items-center">
            {/* "Supersonic Inlets" (redundant with the SUPIN acronym) and the
                license-restriction text were dropped from always-visible
                content — the trigger only has room for one text line
                alongside its hero at the standardized pill width; both moved
                to the title tooltip above. */}
            <span className="flex items-center gap-1 leading-tight">
              NASA SUPIN
              <ChevronDown className="h-3 w-3" />
              <span
                className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500"
                title="License Restrictions apply"
              />
            </span>
          </div>
          {supinTriggerHero && (
            <div className="flex w-10 flex-shrink-0 items-center justify-center self-stretch">
              <img
                src={supinTriggerHero}
                alt=""
                aria-hidden="true"
                className="h-full object-contain"
              />
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={pickerContentClass}
        style={{
          minWidth: 'var(--radix-dropdown-menu-trigger-width)',
        }}
      >
        {SUPIN_INLET_TYPES.map((opt) => {
          const item = (
            <DropdownMenuItem
              key={opt.value}
              disabled={opt.disabled}
              onClick={() => {
                if (opt.disabled) return;
                setVehicleSubTab('nasa-supersonic-inlet');
                setSupinInletType(opt.value);
              }}
              className={cn(
                pickerDisablableItemBaseClass,
                opt.disabled
                  ? pickerItemDisabledClass
                  : 'hover:cursor-pointer hover:bg-[#0f3d24]',
                !opt.disabled && supinInletType === opt.value
                  ? pickerItemActiveClass
                  : pickerItemInactiveClass,
                // Active cards match the SUPIN tab box height;
                // coming-soon rows stay short to read as
                // "not a real option yet".
                opt.disabled
                  ? 'flex h-10 items-center justify-between gap-3'
                  : 'flex h-20 items-center justify-between gap-3',
              )}
            >
              <div className="flex flex-col items-start">
                <span className="font-medium leading-tight">{opt.label}</span>
                <span className="mt-0.5 text-xs leading-tight opacity-80">
                  {opt.subLabel}
                </span>
              </div>
              {opt.hero && (
                <div className={pickerHeroWrapperClass}>
                  <img
                    src={opt.hero}
                    alt=""
                    aria-hidden="true"
                    className="h-full object-contain"
                  />
                </div>
              )}
            </DropdownMenuItem>
          );
          if (!opt.disabled) return item;
          return (
            <Tooltip key={opt.value}>
              <TooltipTrigger asChild>
                {/* span needed: disabled items don't fire
                    pointer events, so the tooltip never opens
                    without a sibling wrapper. */}
                <span className="block">{item}</span>
              </TooltipTrigger>
              <TooltipContent side="right">Coming soon.</TooltipContent>
            </Tooltip>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
